import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Apple, Chrome, Smartphone } from "lucide-react";

/**
 * Guide d'installazione (app webapp, niente store).
 * - variant="stack"  : due pulsanti pieni "Come installare da Safari / Chrome" (login).
 * - variant="compact": card compatta con etichetta + due mini-pulsanti iOS / Android
 *                      (header dashboard, pagina Control). Non disturba gli altri tasti.
 * Il popup usa il Dialog Radix (stesso dell'app): scroll-lock, Esc e click-fuori nativi;
 * alla chiusura il Content viene smontato (iframe rimosso = come about:blank, video fermo).
 */
export const InstallHelpButtons = ({
  variant = "stack",
  label = "Installa come app",
  subtitle = "Niente store · è una webapp",
  className = "",
}) => {
  const [which, setWhich] = useState(null); // 'safari' | 'chrome' | null
  const open = which !== null;
  const src = which ? `/come-installare-hostland.html?solo=${which}` : "about:blank";

  const Modal = (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) setWhich(null); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          data-testid="install-help-modal"
          className="fixed z-[121] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 focus:outline-none
                     w-screen h-[100dvh] max-w-none bg-[#0c1015]
                     sm:w-[92vw] sm:max-w-[1100px] sm:h-auto sm:rounded-2xl sm:shadow-2xl sm:border sm:border-white/10 sm:overflow-hidden
                     data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95
                     data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
        >
          <DialogPrimitive.Title className="sr-only">Come installare Hostland</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Guida passo-passo per installare Hostland come app dal browser.
          </DialogPrimitive.Description>
          <DialogPrimitive.Close
            className="absolute top-3 right-3 z-10 h-11 w-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/15 flex items-center justify-center text-white transition-all active:scale-95 shadow-lg"
            aria-label="Chiudi"
            data-testid="install-help-close-btn"
          >
            <X className="h-6 w-6" />
          </DialogPrimitive.Close>
          <div className="w-full h-full sm:h-auto sm:aspect-video bg-[#0c1015]">
            {open && (
              <iframe
                key={which}
                src={src}
                title="Guida installazione Hostland"
                className="w-full h-full border-0 block"
                allow="autoplay"
              />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );

  if (variant === "compact") {
    return (
      <>
        <style>{`@keyframes ihb-shine{0%{transform:translateX(-130%) skewX(-20deg)}100%{transform:translateX(360%) skewX(-20deg)}}`}</style>
        <div className={`relative overflow-hidden inline-flex flex-wrap items-center gap-x-2 gap-y-1.5 max-w-full rounded-2xl px-2.5 py-1.5 shadow-md shadow-red-600/30 bg-gradient-to-r from-red-600 to-red-500 ${className}`}>
          <span aria-hidden className="pointer-events-none absolute top-0 bottom-0 w-1/3" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent)", animation: "ihb-shine 1.5s linear infinite" }} />
          <div className="relative w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
            <Smartphone className="w-3.5 h-3.5 text-white" strokeWidth={2.2} />
          </div>
          <div className="relative min-w-0 mr-auto">
            <p className="text-[11px] font-black text-white leading-tight">{label}</p>
            <p className="text-[9px] text-white/80 leading-tight">{subtitle}</p>
          </div>
          <div className="relative flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setWhich("safari")}
              className="inline-flex items-center gap-1 rounded-lg border border-white/30 bg-white/15 hover:bg-white/25 text-white font-bold text-[10px] px-2 py-1 transition-all active:scale-95"
              data-testid="install-app-ios-btn"
            >
              <Apple className="w-3 h-3" strokeWidth={2.2} /> iOS
            </button>
            <button
              type="button"
              onClick={() => setWhich("chrome")}
              className="inline-flex items-center gap-1 rounded-lg border border-white/30 bg-white/15 hover:bg-white/25 text-white font-bold text-[10px] px-2 py-1 transition-all active:scale-95"
              data-testid="install-app-android-btn"
            >
              <Chrome className="w-3 h-3" strokeWidth={2.2} /> Android
            </button>
          </div>
        </div>
        {Modal}
      </>
    );
  }

  const baseBtn =
    "w-full rounded-2xl py-3 font-bold text-sm border-2 border-zinc-200 bg-white text-zinc-900 " +
    "hover:border-zinc-900 hover:bg-zinc-50 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-sm";

  return (
    <>
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2.5 ${className}`}>
        <button type="button" onClick={() => setWhich("safari")} className={baseBtn} data-testid="install-help-safari-btn">
          <Apple className="h-4 w-4" strokeWidth={2.2} /> Come installare da Safari
        </button>
        <button type="button" onClick={() => setWhich("chrome")} className={baseBtn} data-testid="install-help-chrome-btn">
          <Chrome className="h-4 w-4" strokeWidth={2.2} /> Come installare da Chrome
        </button>
      </div>
      {Modal}
    </>
  );
};

export default InstallHelpButtons;
